import { useState } from "react";
import axios from "axios";
import { Loader2, Scale, Trophy, AlertCircle, Eye, ThumbsUp, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_LEN = 20;

const fmt = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n);

const ScoreCard = ({ result, isWinner, label, onViewFull }) => (
  <div className={`rounded-2xl p-6 border-2 transition-all ${isWinner ? "border-yellow-400 bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-2xl" : "border-gray-200 bg-white text-gray-800"}`}>
    <div className="flex items-center justify-between mb-3">
      <span className={`text-xs font-bold uppercase tracking-wide ${isWinner ? "text-yellow-300" : "text-gray-400"}`}>{label}</span>
      {isWinner && (
        <span className="inline-flex items-center gap-1 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full">
          <Trophy className="w-3 h-3" /> GANADOR
        </span>
      )}
    </div>
    <h4 className="font-bold text-sm line-clamp-2 mb-1 min-h-[2.5rem]">
      {result.video_metadata?.title || result.script_text?.slice(0, 60) || "Contenido de texto"}
    </h4>
    {result.video_metadata?.channel && (
      <p className={`text-xs mb-3 ${isWinner ? "text-purple-100" : "text-gray-500"}`}>{result.video_metadata.channel}</p>
    )}
    <div className="text-5xl font-extrabold">{result.viral_score}<span className="text-xl font-semibold opacity-70"> /100</span></div>
    <p className={`text-xs mt-1 ${isWinner ? "text-purple-100" : "text-gray-500"}`}>Potencial Viral</p>
    {result.video_metadata && (
      <div className="flex gap-3 mt-3 text-xs">
        <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" />{fmt(result.video_metadata.view_count || 0)}</span>
        <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{fmt(result.video_metadata.like_count || 0)}</span>
      </div>
    )}
    <button
      data-testid={`view-full-${label === "Video A" ? "a" : "b"}`}
      onClick={() => onViewFull(result)}
      className={`mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${isWinner ? "bg-white/20 hover:bg-white/30 text-white" : "bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200"}`}
    >
      <ExternalLink className="w-4 h-4" /> Ver informe completo
    </button>
  </div>
);

export default function ComparePanel({ onViewFull }) {
  const [urlA, setUrlA] = useState("");
  const [urlB, setUrlB] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const handleCompare = async () => {
    if (urlA.trim().length < MIN_LEN || urlB.trim().length < MIN_LEN) {
      setError(`Ingresa una URL de YouTube (o texto de al menos ${MIN_LEN} caracteres) en ambos campos`);
      return;
    }
    setLoading(true);
    setError(null);
    setResults(null);
    const [resA, resB] = await Promise.allSettled([
      axios.post(`${API}/analyze-script`, { script_text: urlA }),
      axios.post(`${API}/analyze-script`, { script_text: urlB }),
    ]);
    setLoading(false);
    if (resA.status === "rejected" || resB.status === "rejected") {
      const which = [resA.status === "rejected" && "Video A", resB.status === "rejected" && "Video B"].filter(Boolean).join(" y ");
      const detail = (resA.status === "rejected" ? resA.reason : resB.reason)?.response?.data?.detail;
      setError(`Error analizando ${which}. ${detail || "Verifica las URLs e intenta de nuevo."}`);
      return;
    }
    setResults([resA.value.data, resB.value.data]);
  };

  const breakdownKeys = results
    ? [...new Set([...Object.keys(results[0].viral_breakdown || {}), ...Object.keys(results[1].viral_breakdown || {})])]
    : [];
  const winnerIdx = results ? (results[1].viral_score > results[0].viral_score ? 1 : 0) : -1;
  const tie = results && results[0].viral_score === results[1].viral_score;

  return (
    <div data-testid="compare-panel">
      <div className="flex items-center gap-2 mb-4">
        <Scale className="w-5 h-5 text-purple-600" />
        <h2 className="text-xl font-semibold text-gray-800">Compara dos videos lado a lado</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {[{ v: urlA, set: setUrlA, l: "Video A" }, { v: urlB, set: setUrlB, l: "Video B" }].map((f) => (
          <div key={f.l}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{f.l}</label>
            <textarea
              data-testid={`compare-input-${f.l === "Video A" ? "a" : "b"}`}
              value={f.v}
              onChange={(e) => f.set(e.target.value)}
              placeholder="URL de YouTube o guion del video..."
              className="w-full h-24 p-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none resize-none text-gray-700 placeholder-gray-400 transition-all text-sm"
              disabled={loading}
            />
          </div>
        ))}
      </div>

      <button
        data-testid="compare-button"
        onClick={handleCompare}
        disabled={loading || urlA.trim().length < MIN_LEN || urlB.trim().length < MIN_LEN}
        className="mt-4 w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Analizando ambos videos... (~40-50 seg)
          </>
        ) : (
          <>
            <Scale className="w-5 h-5" />
            Comparar Videos
          </>
        )}
      </button>

      {error && (
        <div data-testid="compare-error" className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {results && (
        <div data-testid="compare-results" className="mt-8 space-y-6 animate-fadeIn">
          {!tie && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl px-5 py-3 text-center">
              <p className="text-sm font-semibold text-yellow-800">
                🏆 <b>{results[winnerIdx].video_metadata?.title || `Video ${winnerIdx === 0 ? "A" : "B"}`}</b> tiene mayor potencial viral
                ({results[winnerIdx].viral_score} vs {results[1 - winnerIdx].viral_score})
              </p>
            </div>
          )}
          {tie && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl px-5 py-3 text-center">
              <p className="text-sm font-semibold text-blue-800">Empate: ambos videos tienen el mismo potencial viral ({results[0].viral_score}/100)</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <ScoreCard result={results[0]} isWinner={!tie && winnerIdx === 0} label="Video A" onViewFull={onViewFull} />
            <ScoreCard result={results[1]} isWinner={!tie && winnerIdx === 1} label="Video B" onViewFull={onViewFull} />
          </div>

          {breakdownKeys.length > 0 && (
            <div className="bg-white rounded-2xl border border-purple-100 overflow-hidden">
              <div className="grid grid-cols-3 bg-purple-50 px-4 py-3 text-xs font-bold text-purple-700 uppercase">
                <span>Factor</span>
                <span className="text-center">Video A</span>
                <span className="text-center">Video B</span>
              </div>
              {breakdownKeys.map((key) => {
                const a = results[0].viral_breakdown?.[key] ?? 0;
                const b = results[1].viral_breakdown?.[key] ?? 0;
                return (
                  <div key={key} className="grid grid-cols-3 px-4 py-2.5 border-t border-gray-100 text-sm items-center">
                    <span className="font-medium text-gray-700 capitalize">{key.replace(/_/g, " ")}</span>
                    <span className={`text-center font-bold ${a > b ? "text-green-600" : a < b ? "text-gray-400" : "text-gray-600"}`}>{a}</span>
                    <span className={`text-center font-bold ${b > a ? "text-green-600" : b < a ? "text-gray-400" : "text-gray-600"}`}>{b}</span>
                  </div>
                );
              })}
              {(results[0].growth_potential_score || results[1].growth_potential_score) && (
                <div className="grid grid-cols-3 px-4 py-2.5 border-t-2 border-purple-100 text-sm items-center bg-purple-50/50">
                  <span className="font-semibold text-gray-800">Potencial de crecimiento</span>
                  <span className="text-center font-bold text-purple-700">{results[0].growth_potential_score ?? "—"}</span>
                  <span className="text-center font-bold text-purple-700">{results[1].growth_potential_score ?? "—"}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
